import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../firebase'; // Correct relative path
import { doc, onSnapshot, updateDoc } from 'firebase/firestore'; // Add updateDoc to the import
import './EventDetails.css';
import { FaBarcode, FaTicketAlt, FaMapMarkerAlt, FaRegCalendarAlt, FaRegClock, FaMoneyBillAlt,  FaShareAlt } from 'react-icons/fa'; // Combine all icons into one import


// Helper function to convert military time to standard time
export const formatTime = (time) => {
  if (!time) return 'Invalid time';
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const standardHours = hours % 12 || 12; // Convert 0 to 12 for midnight
  return `${standardHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

function EventDetails() {
  const { eventCode } = useParams(); // Get the event code from the URL
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showIntro, setShowIntro] = useState(true); // intro overlay
  const [introFading, setIntroFading] = useState(false);
  const [searchQuery, setSearchQuery] = useState(''); // State for search input
  const [searchResults, setSearchResults] = useState([]); // State for search results
  const [selectedGame, setSelectedGame] = useState(null); // State for the selected game
  const [currentPage, setCurrentPage] = useState(1); // Track the current page
  const [announcement, setAnnouncement] = useState(null); // State for announcement

  const gamesPerPage = 10; // Number of games per page
  const indexOfLastGame = currentPage * gamesPerPage;
  const indexOfFirstGame = indexOfLastGame - gamesPerPage;
  const currentGames = event?.games?.slice(indexOfFirstGame, indexOfLastGame) || [];

  const totalPages = Math.ceil((event?.games?.length || 0) / gamesPerPage);

const handleNextPage = () => {
  if (currentPage < totalPages) {
    setCurrentPage(currentPage + 1);
  }
};

const handlePreviousPage = () => {
  if (currentPage > 1) {
    setCurrentPage(currentPage - 1);
  }
};

useEffect(() => {
  if (!eventCode || typeof eventCode !== 'string') {
    console.error('Invalid event code:', eventCode);
    setLoading(false);
    return;
  }

  const eventRef = doc(db, 'events', eventCode);

  const unsubscribe = onSnapshot(eventRef, async (docSnapshot) => {
    if (docSnapshot.exists()) {
      const eventData = docSnapshot.data();

      // Check if the announcement has expired
      const currentTime = Date.now();
      const announcementTimestamp = eventData.announcementTimestamp;

      if (eventData.announcement && announcementTimestamp) {
        const timeElapsed = currentTime - announcementTimestamp;

        if (timeElapsed > 10000) { // 10 seconds expiration
          try {
            await updateDoc(eventRef, { announcement: null, announcementTimestamp: null });
          } catch (error) {
            console.error('Error deleting expired announcement:', error);
          }
        } else {
          setAnnouncement(eventData.announcement);
        }
      }

      // Set event data
      setEvent(eventData);
      setLoading(false);
    } else {
      console.error('Event not found for code:', eventCode);
      alert('Event not found!');
      setLoading(false);
    }
  }, (error) => {
    console.error('Error fetching event:', error);
    alert('Failed to fetch event details.');
    setLoading(false);
  });
  return () => unsubscribe();
}, [eventCode]);

// Show intro overlay once event data is loaded
useEffect(() => {
  if (!event) return;
  // Start fade after 2s, hide after 2.5s
  const fadeTimer = setTimeout(() => setIntroFading(true), 2000);
  const hideTimer = setTimeout(() => setShowIntro(false), 2500);
  return () => {
    clearTimeout(fadeTimer);
    clearTimeout(hideTimer);
  };
}, [event]);

// Auto-clear announcement after 10 seconds
useEffect(() => {
  if (announcement) {
    const timer = setTimeout(() => setAnnouncement(null), 10000);
    return () => clearTimeout(timer);
  }
}, [announcement]);

useEffect(() => {
  if (totalPages > 1) {
    const interval = setInterval(() => {
      setCurrentPage((prevPage) => (prevPage < totalPages ? prevPage + 1 : 1));
    }, 20000); // Change page every 20 seconds

    return () => clearInterval(interval);
  }
}, [totalPages]); // Re-run if totalPages changes

const handleShareEvent = () => {
  const eventURL = `${window.location.origin}/event/${eventCode}`; // Generate the event link

  if (navigator.share) {
    // Use the Web Share API if supported
    navigator.share({
      title: `Check out this event: ${event?.eventName || 'Event'}`,
      text: `Here's the link to the event: ${eventURL}`,
      url: eventURL,
    }).catch((error) => console.error('Error sharing:', error));
  } else {
    // Fallback: Copy the link to clipboard
    navigator.clipboard.writeText(eventURL).then(() => {
      alert('Event link copied to clipboard!');
    }).catch((error) => {
      console.error('Error copying link:', error);
      alert('Failed to copy the link.');
    });
  }
};


  const handleSearch = () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const query = searchQuery.trim().toLowerCase();
  
    const results = [];
  
    // Ensure event.games exists and is an array
    if (event.games && Array.isArray(event.games)) {
      event.games.forEach((game) => {
        const gameName = String(game?.name || '');
        const serialNumber = String(game?.serialNumber || '');
        const winningTicket = String(game?.winningTicket || '');

        // Check if the game name matches the search query
        if (gameName.toLowerCase().includes(query)) {
          results.push({
            type: 'Game',
            value: gameName,
            details: [{ name: gameName, serialNumber, ticket: winningTicket }],
          });
        }
  
        // Check if the serial number matches the search query
        if (serialNumber.toLowerCase().includes(query)) {
          results.push({
            type: 'Serial Number',
            value: serialNumber,
            details: [{ name: gameName, serialNumber, ticket: winningTicket }],
          });
        }
  
        // Check if the winning ticket matches the search query
        if (winningTicket.toLowerCase().includes(query)) {
          results.push({
            type: 'Winning Ticket',
            value: winningTicket,
            details: [{ name: gameName, serialNumber, ticket: winningTicket }],
          });
        }
      });
    }
  
    setSearchResults(results);
  };

  const handleClearSearch = () => {
    setSearchQuery(''); // Reset the search query
    setSearchResults([]); // Clear the search results
  };

  const handleOpenModal = (game) => {
    setSelectedGame(game); // Set the selected game
  };
  
  const handleCloseModal = () => {
    setSelectedGame(null); // Clear the selected game
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!event) {
    return <div className="error">Event not found!</div>;
  }

  return (
    <>
      {showIntro && event && (
        <div
          className={`intro-overlay ${introFading ? 'fade-out' : ''}`}
          onClick={() => setShowIntro(false)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowIntro(false); }}
        >
          <h1 className="intro-h1">Welcome to {event.eventName}</h1>
        </div>
      )}

      <div className="event-details-container">
      {/* Event Name and Location */}
      <div className="event-header">
        <h1 className="nameofevent">{event.eventName}</h1>
        <p>
          <FaMapMarkerAlt style={{ marginRight: '8px', color: 'gold' }} /> Location: {event.eventLocation}
        </p>
        <p>
    <FaRegClock style={{ marginRight: '8px', color: 'gold' }} /> 
    Start Time: {formatTime(event.schedule?.startTime) || 'No start time provided'}
  </p>
  <p>To search for a game that is being played, go to the bottom of the page, type in the serial number of the game, and hit "search". The game information and winning number will show if the game has been completed.</p>
      </div>

      {/* Games Section */}
      <div className="games-section">
  <h2 className="games-heading">
    <FaTicketAlt style={{ marginRight: '8px', color: 'gold' }} /> <span className="games-text">Games</span>
  </h2>
  <ul>
  {currentGames.map((game, gameIndex) => (
    <li key={gameIndex}>
      <strong>{game.name}</strong>
      <p>
        <FaBarcode style={{ marginRight: '8px', color: 'gold' }} />
        Serial Number: {game.serialNumber}
      </p>
      <p>
        <FaTicketAlt style={{ marginRight: '8px', color: 'gold' }} />
        Winning Ticket(s): {game.winningTicket}
      </p>
      <p>
        <FaMoneyBillAlt style={{ marginRight: '8px', color: 'gold' }} />
        Prize: {game.prize}
      </p>
      <p>
        <FaTicketAlt style={{ marginRight: '8px', color: 'gold' }} />
        Game Type: {game.gameType}
      </p>
      <button
        onClick={() => handleOpenModal(game)}
        className="view-game-button"
      >
        View Game
      </button>
      {game.winningTickets && game.winningTickets.length > 0 && (
        <ul className="winning-tickets-list">
          {game.winningTickets.map((ticket, ticketIndex) => (
            <li key={ticketIndex}>
              Serial Number: {ticket.serialNumber}, Ticket: {ticket.ticket}
            </li>
          ))}
        </ul>
      )}
    </li>
  ))}
</ul>

      {/* Centered announcement modal (auto-cleared after 10s by effect above) */}
      {announcement && (
        <div
          className="announcement-modal"
          role="alert"
          onClick={() => setAnnouncement(null)}
        >
          <div className="announcement-content">
            <h2>Announcement</h2>
            <p>{announcement}</p>
          </div>
        </div>
      )}
  <div className="pagination-controls">
    <button
      onClick={handlePreviousPage}
      disabled={currentPage === 1}
      className="pagination-button"
    >
      Previous
    </button>
    <span>
      Page {currentPage} of {totalPages}
    </span>
    <button
      onClick={handleNextPage}
      disabled={currentPage === totalPages}
      className="pagination-button"
    >
      Next
    </button>
  </div>
</div>

      {/* Schedule Section */}
      <div className="schedule-section">
        <h2>
          <FaRegCalendarAlt style={{ marginRight: '8px', color: 'gold' }} /> Schedule
        </h2>
        {event.scheduleImage ? (
          <img
            src={event.scheduleImage}
            alt="Event Schedule"
            className="schedule-image"
          />
        ) : (
          <div>
            <p>
  <FaRegClock style={{ marginRight: '8px', color: 'gold' }} />
  <strong>Start Time:</strong> {formatTime(event.schedule?.startTime) || 'No start time provided'}
</p>
            <ul>
              {event.schedule?.subFields?.map((subField, index) => (
                <li key={index}>
                  <strong>Time:</strong> {formatTime(subField.time)}, <strong>Prize:</strong> {subField.prize}, <strong>Winning Ticket:</strong> {subField.winningTicket || 'N/A'}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {selectedGame && (
  <div className="modal">
    <div className="modal-content">
      <h2>{selectedGame.name}</h2>
      <p>
        <FaBarcode style={{ marginRight: '8px', color: 'gold' }} />
        Serial Number: {selectedGame.serialNumber}
      </p>
      <p>
        <FaTicketAlt style={{ marginRight: '8px', color: 'gold' }} />
        Winning Ticket: {selectedGame.winningTicket}
      </p>
      <p>
        <FaMoneyBillAlt style={{ marginRight: '8px', color: 'gold' }} />
        Prize: {selectedGame.prize}
      </p>
      <p>
        <FaTicketAlt style={{ marginRight: '8px', color: 'gold' }} />
        Game Type: {selectedGame.gameType}
      </p>
      {selectedGame.winningTickets && selectedGame.winningTickets.length > 0 && (
        <ul className="winning-tickets-list">
          {selectedGame.winningTickets.map((ticket, index) => (
            <li key={index}>
              Serial Number: {ticket.serialNumber}, Ticket: {ticket.ticket}
            </li>
          ))}
        </ul>
      )}
      <button onClick={handleCloseModal} className="close-modal-button">
        Close
      </button>
    </div>
  </div>
)}

      {/* Search Bar */}
      <div className="search-section">
        <h3>Search For Game Winners Here ⬇️</h3>
  <input
    type="text"
    placeholder="Search serial numbers, games, or winning tickets"
    value={searchQuery}
    onChange={(e) => setSearchQuery(e.target.value)}
    className="search-input"
  />
  <button onClick={handleSearch} className="search-button">
    Search
  </button>
  <button onClick={handleClearSearch} className="clear-search-button">
    Clear Search
  </button>
  <button onClick={handleShareEvent} className="share-button">
    <FaShareAlt style={{ marginRight: '8px' }} /> Share This Event
  </button>
  <div className="search-results">
    {searchResults.length > 0 ? (
      <ul>
        {searchResults.map((result, index) => (
          <li key={index} className="search-result-item">
            <strong>{result.type}:</strong> {result.value}
            {result.details && result.details.length > 0 && (
              <div className="game-details">
                {result.details.map((detail, detailIndex) => (
                  <div key={detailIndex} className="game-detail">
                    <p>
                      <FaTicketAlt style={{ marginRight: '8px', color: 'gold' }} />
                      Game: {detail.name || 'N/A'}
                    </p>
                    <p>
                      <FaBarcode style={{ marginRight: '8px', color: 'gold' }} />
                      Serial Number: {detail.serialNumber || 'N/A'}
                    </p>
                    <p>
                      <FaTicketAlt style={{ marginRight: '8px', color: 'gold' }} />
                      Winning Ticket: {detail.ticket || 'N/A'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    ) : (
      <p>No results found.</p>
    )}
  </div>
</div>
    </div>
    </>
  );
}

export default EventDetails;